import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Breakpoints } from '@rd-lib/breakpoints';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { RCV2_CHECKOUT_LINK_ACCOUNT_DIALOG } from '@rd/core/tokens/redeem-cash-v2.token';
import { EnrollmentHelper } from '@rd/helpers/enrollment.helper';
import { DialogScreenMode } from '@rd/popup/dialog-config';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import {
  CashBackMembership,
  CashBackToCardMembership,
  LoyaltyProgram,
  LoyaltyProgramCategory,
} from '@rd/types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type ComponentRenderState = 'loading' | 'content';

@Component({
  selector: 'rd-rcv2-checkout-linked-account',
  templateUrl: './checkout-linked-account.component.html',
  styleUrls: ['./checkout-linked-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RcV2CheckoutLinkedAccountComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-rcv2-checkout-linked-account') componentClass = true;
  @Input() iconName: string;
  @Input() isLoggedIn: boolean;
  @Input() loading: boolean;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() redeemCashMembership: CashBackMembership;

  private destroy$ = new Subject();
  private isMobileViewport: boolean;

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  private rcv2CheckoutLinkAccountDialogComponentType = inject(
    RCV2_CHECKOUT_LINK_ACCOUNT_DIALOG,
  );

  constructor(
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private enrollmentHelper: EnrollmentHelper,
    private iconRegistryService: IconRegistryService,
    private translateService: TranslateService,
    private windowResizeService: WindowResizeService,
  ) {}

  ngOnInit(): void {
    this.iconRegistryService.registerIcon('arrow-right');
    this.detectViewportSize();

    this.windowResizeService.windowResizeObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.detectViewportSize());
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  openLinkAccountDialog(): void {
    const screenMode: DialogScreenMode = this.isMobileViewport
      ? 'fullscreen'
      : 'center-md';

    if (this.isLoggedIn) {
      this.dialogService.open(this.rcv2CheckoutLinkAccountDialogComponentType, {
        id: 'rcv2-checkout-link-account-dialog',
        screenMode,
        ariaLabel: this.translateService.instant(
          'redeem_cash_v2.checkout.link_preferred_account',
        ),
      });
    } else {
      this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
        id: 'auth-secure-route-dialog',
        screenMode: 'center-md',
        ariaLabel: this.translateService.instant(
          'accessibility.core.auth_dialog_aria_label',
        ),
        keepModalOnRouteChange: true,
      });
    }
  }

  getComponentRenderState(): ComponentRenderState {
    if (this.loading) {
      return 'loading';
    } else {
      return 'content';
    }
  }

  displayedAccountNumber(): string {
    return this.enrollmentHelper.displayedAccountNumber(
      // @ts-ignore
      this.redeemCashMembership.number,
    );
  }

  get isRedeemCashToCardType(): boolean {
    return (
      this.redeemCashMembership.category ===
      LoyaltyProgramCategory.CashbackToCard
    );
  }

  get groupName(): string {
    return (this.redeemCashMembership as CashBackToCardMembership).details
      .groupName;
  }

  get linkedToAccountLabelTranslationParam(): any {
    return { loyaltyProgram: this.loyaltyProgram.shortName };
  }

  private detectViewportSize(): void {
    const currentIsMobileViewport = this.isMobileViewport;
    this.isMobileViewport = window.innerWidth < Breakpoints.DESKTOP;

    if (this.isMobileViewport !== currentIsMobileViewport) {
      this.cdRef.detectChanges();
    }
  }
}
